import { includes, isNil, map, values } from "lodash";
import { AICC_LANGUAGE_STATUS, AiccLanguageStatus } from "constants/aiccLanguage";
import { AiccPromptGroup, AiccPromptScope, AiccPromptSource } from "constants/aiccPrompt";
import {
  AiccPromptConfigResponse,
  AiccPromptDetailResponse,
  AiccPromptListItemResponse,
  AiccPromptListResponse,
  AiccPromptModelResponse,
  AiccPromptSharedModelResponse,
  AiccPromptVariableResponse,
  AiccPromptVersionGroupResponse,
} from "../dto/response/aiccPrompt";
import {
  AiccPrompt,
  AiccPromptConfig,
  AiccPromptList,
  AiccPromptModel,
  AiccPromptSharedModel,
  AiccPromptSummary,
  AiccPromptVariable,
  AiccPromptVersionGroup,
} from "../model/aiccPrompt";

export function transformAiccPromptVariable(response: AiccPromptVariableResponse): AiccPromptVariable {
  return {
    name: response.name,
    description: response.description ?? "",
    // Absent rather than empty, so the panel can leave the example line out instead of printing a gap.
    exampleValue: response.example_value || undefined,
    // Null means OPTIONAL, the opposite of the asset lists' default, and the backend says so explicitly:
    // nobody has classified the variable. Guessing "required" would block saving on that guess, and the
    // block is the one thing here that stops an admin from working.
    isRequired: response.is_required ?? false,
  };
}

/**
 * A payload as the screen reads it.
 *
 * Nothing is substituted for an absent tunable: the base-set default carries no model and no
 * temperature, and a zero there would read as a real, strictest-possible setting.
 */
export function transformAiccPromptConfig(response: AiccPromptConfigResponse): AiccPromptConfig {
  return {
    modelId: response.model_id || undefined,
    temperature: isNil(response.temperature) ? undefined : response.temperature,
    reasoningEffort: response.llm_reasoning_effort || undefined,
    promptText: response.prompt_text ?? "",
  };
}

export function transformAiccPromptSharedModel(response: AiccPromptSharedModelResponse): AiccPromptSharedModel {
  return {
    key: response.key,
    name: response.name,
    group: response.group as AiccPromptGroup,
    isCrossTab: response.cross_tab ?? false,
  };
}

export function transformAiccPromptSummary(response: AiccPromptListItemResponse): AiccPromptSummary {
  return {
    key: response.key,
    name: response.name,
    description: response.description || undefined,
    group: response.group as AiccPromptGroup,
    source: response.source as AiccPromptSource,
    sourceScope: (response.source_scope as AiccPromptScope) || undefined,
    modelId: response.model_id || undefined,
    temperature: isNil(response.temperature) ? undefined : response.temperature,
    reasoningEffort: response.llm_reasoning_effort || undefined,
    sharesModelWith: map(response.shares_model_with, transformAiccPromptSharedModel),
    // Empty rather than absent: both are rendered as EMPTY, and a string keeps the column's sort and
    // search from having to allow for two kinds of nothing.
    lastModifiedBy: response.last_modified_by ?? "",
    lastModifiedAt: response.last_modified_at ?? "",
  };
}

export function transformAiccPrompt(response: AiccPromptDetailResponse): AiccPrompt {
  return {
    ...transformAiccPromptSummary(response),
    /**
     * 🔴 A NULL COPY STAYS ABSENT. Each of the three drives a decision on the screen — whether to render an
     * editor, what the left column compares against, whether Reset is offered — and an empty config
     * substituted for a missing one would answer yes to all three and then show a blank prompt.
     */
    workingConfig: response.working_config ? transformAiccPromptConfig(response.working_config) : undefined,
    publishedConfig: response.published_config ? transformAiccPromptConfig(response.published_config) : undefined,
    defaultConfig: response.default_config ? transformAiccPromptConfig(response.default_config) : undefined,
    variables: map(response.variables, transformAiccPromptVariable),
    textUpdatedAt: response.text_updated_at || undefined,
    /**
     * 🔴 A WORD THIS BUILD DOES NOT KNOW STAYS ABSENT. The badge looks its colour and label up by this
     * value, so an unrecognised one renders an uncoloured chip with `undefined` written in it. Absent
     * instead, and the screen falls back to the status the link carried — which is also what a backend
     * deployed before the field gives, so the two cases behave alike rather than one of them breaking.
     */
    status: includes(values(AICC_LANGUAGE_STATUS), response.status)
      ? (response.status as AiccLanguageStatus)
      : undefined,
  };
}

export function transformAiccPromptVersionGroup(response: AiccPromptVersionGroupResponse): AiccPromptVersionGroup {
  return {
    scope: (response.scope as AiccPromptScope) || undefined,
    // Absent rather than zero: the draft has no version until it is published, and a 0 would read as one.
    version: isNil(response.version) ? undefined : response.version,
    status: response.status as AiccLanguageStatus,
    prompts: map(response.prompts, transformAiccPromptSummary),
  };
}

export function transformAiccPromptList(response: AiccPromptListResponse): AiccPromptList {
  return {
    // 🔴 The backend's order is kept: published first, draft second, the same as the other three asset
    // endpoints. Sorting here would let one of the four screens disagree with the others about which
    // copy a tab means, which is not something an operator can see.
    versions: map(response?.versions, transformAiccPromptVersionGroup),
    // Absent reads as "no draft": a missing field must not be what offers an edit.
    isLanguageDraftOpen: response?.language_draft_open ?? false,
  };
}

export function transformAiccPromptModel(response: AiccPromptModelResponse): AiccPromptModel {
  return {
    id: response.id,
    name: response.name || response.id,
    isDefault: response.is_default ?? false,
    // Empty means the model has no reasoning at all, so the control for it is hidden rather than
    // offered empty. Sending an effort a model does not accept is a hard 400.
    efforts: response.efforts ?? [],
    isPriced: response.is_priced ?? false,
    // Absent rather than zero: "free" and "not priced" are different answers, and the cost dialog says
    // which one it is rather than printing 0.
    inputPerMillion: isNil(response.input_per_1m) ? undefined : response.input_per_1m,
    cachedInputPerMillion: isNil(response.cached_input_per_1m) ? undefined : response.cached_input_per_1m,
    cacheWritePerMillion: isNil(response.cache_write_per_1m) ? undefined : response.cache_write_per_1m,
    outputPerMillion: isNil(response.output_per_1m) ? undefined : response.output_per_1m,
    currency: response.currency || undefined,
    isDeprecated: response.is_deprecated ?? false,
    // All three absent rather than empty strings: the picker asks "is there something to say", and ""
    // would answer yes and then render a blank line.
    deprecationNote: response.deprecation_note || undefined,
    shutdownAt: response.shutdown_at || undefined,
    // An array, not undefined: "no successor named" and "none sent" are the same answer to the one
    // question a caller asks of it, and a list spares every call site a null guard.
    recommendedAlternatives: response.recommended_alternatives ?? [],
    note: response.note || undefined,
  };
}
